import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, popularTags } from '@/mocks/comics';
import { useState, useEffect } from 'react';

const baseUrl = 'https://hanmandq.com';

const categoryOgImages: Record<string, string> = {
  love: 'https://readdy.ai/api/search-image?query=Korean%20romance%20manhwa%20genre%20illustration%20collage%2C%20warm%20pink%20and%20peach%20artistic%20style%2C%20clean%20professional%20banner%2C%20romantic%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-love&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Korean%20fantasy%20manhwa%20genre%20illustration%20collage%2C%20magical%20and%20epic%20artistic%20style%2C%20clean%20professional%20banner%2C%20fantasy%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Korean%20action%20manhwa%20genre%20illustration%20collage%2C%20dynamic%20and%20powerful%20artistic%20style%2C%20clean%20professional%20banner%2C%20action%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-action&orientation=landscape',
  mystery: 'https://readdy.ai/api/search-image?query=Korean%20mystery%20thriller%20manhwa%20genre%20illustration%20collage%2C%20dark%20and%20intriguing%20artistic%20style%2C%20clean%20professional%20banner%2C%20mystery%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-mystery&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Korean%20comedy%20manhwa%20genre%20illustration%20collage%2C%20bright%20and%20playful%20artistic%20style%2C%20clean%20professional%20banner%2C%20funny%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-comedy&orientation=landscape',
  drama: 'https://readdy.ai/api/search-image?query=Korean%20drama%20manhwa%20genre%20illustration%20collage%2C%20emotional%20and%20deep%20artistic%20style%2C%20clean%20professional%20banner%2C%20dramatic%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-drama&orientation=landscape',
  bl: 'https://readdy.ai/api/search-image?query=Korean%20BL%20romance%20manhwa%20genre%20illustration%20collage%2C%20soft%20and%20elegant%20artistic%20style%2C%20clean%20professional%20banner%2C%20romantic%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-bl&orientation=landscape',
  gl: 'https://readdy.ai/api/search-image?query=Korean%20GL%20romance%20manhwa%20genre%20illustration%20collage%2C%20gentle%20and%20beautiful%20artistic%20style%2C%20clean%20professional%20banner%2C%20romantic%20Korean%20comic%20aesthetic&width=1200&height=630&seq=hanman-cat-gl&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const comics = getComicsByCategory(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading text-foreground-900">分类未找到</h1>
        </div>
        <Footer />
      </>
    );
  }

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return b.updateDate.localeCompare(a.updateDate);
  });

  const totalPages = Math.ceil(sortedComics.length / perPage);
  const paginatedComics = sortedComics.slice((currentPage - 1) * perPage, currentPage * perPage);

  const catUrl = `${baseUrl}/category/${slug}`;
  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩漫大全`,
    description: `韩漫大全${category.name}分类，收录${category.count}部优质韩国漫画，每日更新最新章节，免费在线阅读。`,
    url: catUrl,
    isPartOf: { '@id': `${baseUrl}/#website` },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': baseUrl, name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': catUrl, name: category.name } },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${category.name}漫画-韩漫大全 | 免费韩国漫画${category.name}分类在线阅读`}
        description={`韩漫大全${category.name}分类，收录${category.count}部优质韩国${category.name}漫画，每日更新最新章节，免费在线阅读。支持评论收藏，随时随地畅享韩漫阅读乐趣。`}
        keywords={`韩漫大全,${category.name}漫画,韩国漫画${category.name},免费${category.name}漫画,${category.name}类韩漫`}
        canonicalUrl={catUrl}
        ogImage={categoryOgImages[slug || ''] || ''}
        schemaData={[categorySchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  {category.name}漫画
                </h1>
                <p className="mt-1 text-sm text-foreground-500">
                  共 {category.count} 部作品
                </p>
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                {([
                  { key: 'latest' as const, label: '最新' },
                  { key: 'popular' as const, label: '最热' },
                  { key: 'rating' as const, label: '评分' },
                ]).map((item) => (
                  <button
                    key={item.key}
                    onClick={() => { setSortBy(item.key); setCurrentPage(1); }}
                    className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      sortBy === item.key
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {popularTags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {tag.name}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}